import { Button } from "primereact/button";
import { useNavigate } from "react-router-dom";
import { TEmployeeFormData } from "../Employee.model";
import { useEmployeeForm } from "./EmployeeForm.hooks";
import { FormFieldsPanel } from "../../../components/form-fields-panel/form-fields-panel";
import { LabelValue } from "../../../components/LabelValue/LabelValue";
import { Controller } from "react-hook-form";
import { InputText } from "primereact/inputtext";
import { InputMask } from "primereact/inputmask";
import { InputNumber } from "primereact/inputnumber";
import { classNames as cx } from "primereact/utils";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { alertOptions } from "./helpers";
import { InputTextarea } from "primereact/inputtextarea";
import { FileUpload } from "primereact/fileupload";
import { useEffect, useRef, useState } from "react";
import { If } from "../../../components/If/If";
import { EmailChangeConfirmDialog } from "../../../components/EmailChangeConfirmDialog/EmailChangeConfirmDialog";

import classes from "./EmployeeForm.module.scss";
import { requests } from "../../../services/http-common";
import { AttachMultipleDocsPopup } from "../../../components/AttachMultipleDocsPopup/AttachMultipleDocsPopup";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import {
  onlyLettersAndSpacesRegex,
  onlyPhoneCharsRegex,
} from "../../../constants/regex";
import { EmployeeRoles } from "../../../models/Employee";
import { useUSAStates } from "../../../helpers/helperHooks";

type EmployeeFormProps = {
  onSubmit: (props: TEmployeeFormData) => void;
  employeeId?: string;
  isPending: boolean;
};

export const EmployeeForm = ({
  onSubmit,
  employeeId,
  isPending,
}: EmployeeFormProps) => {
  const navigate = useNavigate();

  const { hookForm, userRoleOptions, cities } = useEmployeeForm(employeeId);

  const states = useUSAStates();

  const [openMultipleDocsPopup, setOpenMultipleDocsPopup] = useState(false);
  const [pendingData, setPendingData] = useState<any | null>(null);

  const pdfFileUploadRef = useRef(null);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, dirtyFields },
  } = hookForm;

  const [pdfAttachmentsArr, otherDocumentsArr, role, accountNumber] = watch([
    "pdfAttachmentsArr",
    "otherDocumentsArr",
    "role",
    "accountNumber",
  ]);

  const getFormErrorMessage = (name: any) => {
    return (
      // ✅ THIS IS OK
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      errors[name] && <small className="p-error">{errors[name].message}</small>
    );
  };

  const handleClearPdfAttachemnt = () => {
    if (employeeId && pdfAttachmentsArr) {
      //@ts-ignore
      handleDeleteAttachment("pdfAttachmentsArr", [pdfAttachmentsArr[0].path]);
    }
    setValue("pdfAttachmentsArr", null);
    //@ts-ignore
    pdfFileUploadRef.current?.clear();
  };

  const handleAddOtherDocuments = (files: File[]) => {
    setValue(
      "otherDocumentsArr",
      otherDocumentsArr ? [...otherDocumentsArr, ...files] : [...files]
    );
  };

  const handleDeleteAttachment = async (
    fileType: "pdfAttachmentsArr" | "otherDocumentsArr",
    filePaths: string[]
  ) => {
    requests.delete(`/user/${employeeId}/attachment`, {
      data: { [fileType]: filePaths },
    });
  };

  const submitEmployee = async (
    employeeForm: TEmployeeFormData,
    notifyEmailChange?: boolean
  ) => {
    if (employeeForm.ssn) {
      employeeForm.ssn = employeeForm.ssn.replace(/-/g, "");
    }

    let attachmentFilePath = "";
    let otherDocumentPaths = [];

    if (pdfAttachmentsArr && pdfAttachmentsArr[0]?.size) {
      const formData = new FormData();
      formData.append("attachment", pdfAttachmentsArr[0]);
      formData.append("email", employeeForm.email);
      formData.append("type", "pdfAttachment");

      const pdfDocPath: string = await requests.post(
        "/user/attachment",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          transformRequest: (formData: any) => formData,
        }
      );
      attachmentFilePath = pdfDocPath;
    }
    if (otherDocumentsArr) {
      const otherDocPromises: Promise<any>[] = [];

      otherDocumentsArr.forEach((doc) => {
        if (doc.size) {
          const formData = new FormData();
          formData.append("attachment", doc);
          formData.append("email", employeeForm.email);
          formData.append("type", "otherDocument");

          otherDocPromises.push(
            requests.post("/user/attachment", formData, {
              headers: { "Content-Type": "multipart/form-data" },
              transformRequest: (formData: any) => formData,
            })
          );
        }
      });

      const otherDocPaths = await Promise.all(otherDocPromises);
      otherDocumentPaths = otherDocPaths;
    }

    const existingOtherDocPaths =
      otherDocumentsArr
        ?.filter((doc) => !doc.lastModified)
        .map((doc: any) => doc.path) || [];

    const payload = {
      ...employeeForm,
      pdfAttachmentsArr: attachmentFilePath ? [attachmentFilePath as any] : [],
      otherDocumentsArr: [...existingOtherDocPaths, ...otherDocumentPaths],
    };

    onSubmit(
      notifyEmailChange === undefined
        ? payload
        : { ...payload, notifyEmailChange }
    );
  };

  const handleSave = async (employeeForm: TEmployeeFormData) => {
    if (employeeId && dirtyFields?.email) {
      setPendingData(employeeForm);
      return;
    }

    await submitEmployee(employeeForm);
  };

  useEffect(() => {
    if (role === EmployeeRoles.CLEANING_TECHNICIAN) {
      setValue("supervisorAlerts", undefined);
    }
  }, [role]);

  return (
    <form onSubmit={handleSubmit(handleSave)} className={classes.form}>
      <div className={classes.fieldsContainer}>
        <FormFieldsPanel label="General information">
          <div className={classes.fieldsGroupContent}>
            <div className={classes.fieldsColumn}>
              <LabelValue label="First Name" isRequired>
                <Controller
                  name="firstName"
                  render={({ field }) => (
                    <InputText
                      {...field}
                      tabIndex={1}
                      keyfilter={onlyLettersAndSpacesRegex}
                      placeholder="First Name"
                      className={cx({ "p-invalid": errors.firstName })}
                    />
                  )}
                  control={control}
                />
                {getFormErrorMessage("firstName")}
              </LabelValue>
              <LabelValue label="Position">
                <Controller
                  name="role"
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      appendTo="self"
                      id={field.name}
                      tabIndex={4}
                      value={field.value}
                      onChange={(e) => field.onChange(e.value)}
                      options={userRoleOptions}
                      placeholder="Position"
                      style={{ minWidth: "203px" }}
                      className={cx({
                        "p-invalid": errors.role,
                      })}
                    />
                  )}
                />
                {getFormErrorMessage("role")}
              </LabelValue>
              <LabelValue label="State">
                <Controller
                  name="state"
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      appendTo="self"
                      id={field.name}
                      tabIndex={7}
                      value={field.value}
                      onChange={(e) => {
                        setValue("city", "");
                        field.onChange(e.value);
                      }}
                      options={states}
                      placeholder="State"
                      style={{ minWidth: "203px" }}
                      className={cx({
                        "p-invalid": errors.state,
                      })}
                    />
                  )}
                />
                {getFormErrorMessage("state")}
              </LabelValue>
              <LabelValue label="Address Line 1">
                <Controller
                  name="addressLine1"
                  render={({ field }) => (
                    <InputText
                      {...field}
                      tabIndex={10}
                      placeholder="Address Line 1"
                      className={cx({ "p-invalid": errors.addressLine1 })}
                    />
                  )}
                  control={control}
                />
                {getFormErrorMessage("addressLine1")}
              </LabelValue>
              <LabelValue label="Work Phone">
                <Controller
                  name="workPhone"
                  render={({ field }) => (
                    <InputText
                      {...field}
                      keyfilter={onlyPhoneCharsRegex}
                      placeholder="Work Phone"
                      tabIndex={13}
                      className={cx({ "p-invalid": errors.workPhone })}
                    />
                  )}
                  control={control}
                />
                {getFormErrorMessage("workPhone")}
              </LabelValue>
              <LabelValue label="Pay Rate">
                <Controller
                  name="payRate"
                  render={({ field }) => (
                    <InputNumber
                      value={field.value}
                      onValueChange={(e) => field.onChange(e.value)}
                      placeholder="$ Pay Rate"
                      inputId="currency-us"
                      tabIndex={16}
                      mode="currency"
                      currency="USD"
                      locale="en-US"
                      className={cx({ "p-invalid": errors.payRate })}
                    />
                  )}
                  control={control}
                />
                {getFormErrorMessage("payRate")}
              </LabelValue>
              <LabelValue label="DL N">
                <Controller
                  name="dlStateN"
                  render={({ field }) => (
                    <InputText
                      {...field}
                      tabIndex={19}
                      keyfilter="alphanum"
                      placeholder="DL N"
                      className={cx({ "p-invalid": errors.dlStateN })}
                    />
                  )}
                  control={control}
                />
                {getFormErrorMessage("dlStateN")}
              </LabelValue>
            </div>
            <div className={classes.fieldsColumn}>
              <LabelValue label="Last Name" isRequired>
                <Controller
                  name="lastName"
                  render={({ field }) => (
                    <InputText
                      {...field}
                      tabIndex={2}
                      keyfilter={onlyLettersAndSpacesRegex}
                      placeholder="Last Name"
                      className={cx({ "p-invalid": errors.lastName })}
                    />
                  )}
                  control={control}
                />
                {getFormErrorMessage("lastName")}
              </LabelValue>
              <LabelValue label="Birth date">
                <Controller
                  name="birthDate"
                  control={control}
                  render={({ field }) => (
                    <Calendar
                      id={field.name}
                      value={field.value}
                      tabIndex={5}
                      onChange={(e) => field.onChange(e.value)}
                      placeholder="MM/DD/YY"
                      dateFormat="mm/dd/yy"
                      showIcon
                      showButtonBar
                      className={cx({ "p-invalid": errors.birthDate })}
                      style={{ maxWidth: "203px" }}
                    />
                  )}
                />
                {getFormErrorMessage("birthDate")}
              </LabelValue>
              <LabelValue label="City">
                <Controller
                  name="city"
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      appendTo="self"
                      id={field.name}
                      tabIndex={8}
                      value={field.value}
                      onChange={(e) => field.onChange(e.value)}
                      options={cities}
                      disabled={!cities?.length}
                      placeholder="City"
                      style={{ minWidth: "203px" }}
                      className={cx({
                        "p-invalid": errors.city,
                      })}
                    />
                  )}
                />
                {getFormErrorMessage("city")}
              </LabelValue>
              <LabelValue label="Address Line 2">
                <Controller
                  name="addressLine2"
                  render={({ field }) => (
                    <InputText
                      {...field}
                      tabIndex={11}
                      placeholder="Address Line 2"
                      className={cx({ "p-invalid": errors.addressLine2 })}
                    />
                  )}
                  control={control}
                />
                {getFormErrorMessage("addressLine2")}
              </LabelValue>
              <LabelValue label="Emergency Contact Number">
                <Controller
                  name="emergencyContact"
                  render={({ field }) => (
                    <InputText
                      {...field}
                      tabIndex={14}
                      keyfilter={onlyPhoneCharsRegex}
                      placeholder="Emergency Contact Number"
                    />
                  )}
                  control={control}
                />
              </LabelValue>
              <LabelValue label="Overtime Rate">
                <Controller
                  name="overTimeRate"
                  render={({ field }) => (
                    <InputNumber
                      value={field.value}
                      onValueChange={(e) => field.onChange(e.value)}
                      placeholder="$ Overtime Rate "
                      inputId="currency-us"
                      mode="currency"
                      tabIndex={17}
                      currency="USD"
                      locale="en-US"
                      className={cx({ "p-invalid": errors.overTimeRate })}
                    />
                  )}
                  control={control}
                />
                {getFormErrorMessage("overTimeRate")}
              </LabelValue>
              <LabelValue label="Social Security Number">
                <Controller
                  name="ssn"
                  render={({ field }) => (
                    <InputMask
                      {...field}
                      maxLength={9}
                      mask="999-99-9999"
                      tabIndex={20}
                      placeholder="Social Security Number"
                      className={cx({
                        "p-invalid": errors.ssn,
                      })}
                    />
                  )}
                  control={control}
                />
                {getFormErrorMessage("ssn")}
              </LabelValue>
            </div>
            <div className={classes.fieldsColumn}>
              <LabelValue label="Email Address" isRequired>
                <Controller
                  name="email"
                  render={({ field }) => (
                    <InputText
                      {...field}
                      tabIndex={3}
                      placeholder="Email Address"
                      className={cx({ "p-invalid": errors.email })}
                    />
                  )}
                  control={control}
                />
                {getFormErrorMessage("email")}
              </LabelValue>
              <LabelValue label="Hire Date">
                <Controller
                  name="hireDate"
                  control={control}
                  render={({ field }) => (
                    <Calendar
                      id={field.name}
                      value={field.value}
                      tabIndex={6}
                      onChange={(e) => field.onChange(e.value)}
                      placeholder="MM/DD/YY"
                      dateFormat="mm/dd/yy"
                      showIcon
                      showButtonBar
                      className={cx({ "p-invalid": errors.hireDate })}
                      style={{ maxWidth: "203px" }}
                    />
                  )}
                />
                {getFormErrorMessage("hireDate")}
              </LabelValue>
              <LabelValue label="ZIP">
                <Controller
                  name="ZIP"
                  render={({ field }) => (
                    <InputText
                      {...field}
                      placeholder="ZIP"
                      tabIndex={9}
                      keyfilter="int"
                      className={cx({ "p-invalid": errors.ZIP })}
                    />
                  )}
                  control={control}
                />
                {getFormErrorMessage("ZIP")}
              </LabelValue>
              <LabelValue label="Home Phone">
                <Controller
                  name="homePhone"
                  render={({ field }) => (
                    <InputText
                      {...field}
                      placeholder="Home Phone"
                      tabIndex={12}
                      keyfilter={onlyPhoneCharsRegex}
                      className={cx({ "p-invalid": errors.homePhone })}
                    />
                  )}
                  control={control}
                />
                {getFormErrorMessage("homePhone")}
              </LabelValue>
              <LabelValue label="Emergency Contact Name">
                <Controller
                  name="emergencyContactName"
                  render={({ field }) => (
                    <InputText
                      {...field}
                      tabIndex={15}
                      keyfilter={onlyLettersAndSpacesRegex}
                      placeholder="Emergency Contact Name"
                    />
                  )}
                  control={control}
                />
              </LabelValue>
              <LabelValue label="Desired Hours per week">
                <Controller
                  name="desiredHoursPerWeek"
                  render={({ field }) => (
                    <InputNumber
                      value={field.value}
                      onValueChange={(e) => field.onChange(e.value)}
                      placeholder="Desired Hours per week"
                      tabIndex={18}
                      className={cx({
                        "p-invalid": errors.desiredHoursPerWeek,
                      })}
                      useGrouping={false}
                    />
                  )}
                  control={control}
                />
                {getFormErrorMessage("desiredHoursPerWeek")}
              </LabelValue>
            </div>
          </div>
        </FormFieldsPanel>
        <FormFieldsPanel label="Banking information">
          <div className={classes.fieldsGroupContent}>
            <div className={classes.fieldsColumn}>
              <LabelValue label="Account Name">
                <Controller
                  name="accountName"
                  render={({ field }) => (
                    <InputText
                      {...field}
                      placeholder="Account Name"
                      tabIndex={21}
                      keyfilter={onlyLettersAndSpacesRegex}
                      className={cx({ "p-invalid": errors.accountName })}
                    />
                  )}
                  control={control}
                />
                {getFormErrorMessage("accountName")}
              </LabelValue>
            </div>
            <div className={classes.fieldsColumn}>
              <LabelValue label="Routing Number">
                <Controller
                  name="routingNumber"
                  render={({ field }) => (
                    <InputText
                      {...field}
                      keyfilter="int"
                      tabIndex={22}
                      placeholder="Routing Number"
                      className={cx({ "p-invalid": errors.routingNumber })}
                    />
                  )}
                  control={control}
                />
                {getFormErrorMessage("routingNumber")}
              </LabelValue>
            </div>
            <div className={classes.fieldsColumn}>
              <LabelValue label="Account Number">
                <Controller
                  name="accountNumber"
                  render={({ field }) => (
                    <InputText
                      {...field}
                      keyfilter="int"
                      tabIndex={23}
                      placeholder="Account Number"
                      className={cx({ "p-invalid": errors.accountNumber })}
                    />
                  )}
                  control={control}
                />
                {getFormErrorMessage("accountNumber")}
              </LabelValue>
            </div>
            <div
              className={classes.fieldsColumn}
              style={{ marginTop: 10, marginBottom: 10 }}
            >
              <LabelValue
                label="Confirm Account Number"
                isRequired={Boolean(accountNumber)}
              >
                <Controller
                  name="confirmAccountNumber"
                  render={({ field }) => (
                    <InputText
                      {...field}
                      keyfilter="int"
                      tabIndex={23}
                      placeholder="Confirm Account Number"
                      className={cx({
                        "p-invalid": errors.confirmAccountNumber,
                      })}
                    />
                  )}
                  control={control}
                />
                {getFormErrorMessage("confirmAccountNumber")}
              </LabelValue>
            </div>
          </div>
        </FormFieldsPanel>
        <FormFieldsPanel label="Additional information">
          <div className={classes.fieldsGroupContent}>
            <LabelValue label="Notes">
              <Controller
                name="notes"
                render={({ field }) => (
                  <InputTextarea
                    {...field}
                    tabIndex={24}
                    placeholder="Notes"
                    rows={5}
                  />
                )}
                control={control}
              />
            </LabelValue>
            {role !== EmployeeRoles.CLEANING_TECHNICIAN ? (
              <LabelValue label="Supervisor Alerts">
                <Controller
                  name="supervisorAlerts"
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      appendTo="self"
                      id={field.name}
                      tabIndex={25}
                      value={field.value}
                      onChange={(e) => field.onChange(e.value)}
                      options={alertOptions}
                      placeholder="Supervisor Alerts"
                      style={{ minWidth: "203px" }}
                    />
                  )}
                />
              </LabelValue>
            ) : null}
            <div className={classes.checkbox}>
              <Controller
                name="mobileAccess"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    inputId={field.name}
                    checked={field.value}
                    inputRef={field.ref}
                    tabIndex={26}
                    onChange={(e) => field.onChange(e.checked)}
                  />
                )}
              />
              <label htmlFor={"mobileAccess"}>Mobile Access</label>
            </div>
          </div>

          <div className={classes.attachments}>
            <LabelValue label="PDF attachment background screening">
              <If
                condition={
                  !!(pdfAttachmentsArr && pdfAttachmentsArr.length > 0)
                }
              >
                <div className={classes.pdfAttachemntLabel}>
                  <span className={classes.name}>
                    {pdfAttachmentsArr && pdfAttachmentsArr[0]?.name}
                  </span>
                  <Button
                    icon="pi pi-times"
                    className=" p-button-sm p-button-rounded p-button-danger p-button-text"
                    aria-label="Cancel"
                    onClick={handleClearPdfAttachemnt}
                  />
                </div>
                <FileUpload
                  ref={pdfFileUploadRef}
                  mode="basic"
                  accept="application/pdf"
                  customUpload
                  onSelect={(e) => setValue("pdfAttachmentsArr", [e.files[0]])}
                />
              </If>
            </LabelValue>
            <div className={classes.otherDocsContainer}>
              <div className={classes.otherDocsTitle}>
                <span>Other Documents</span>
                <Button
                  label="Add files"
                  icon="pi pi-plus"
                  type="button"
                  className="p-button-text"
                  onClick={() => setOpenMultipleDocsPopup(true)}
                />
              </div>
              <div className={classes.docList}>
                <DataTable value={otherDocumentsArr || []}>
                  <Column
                    header="Name"
                    body={(rowData) => <span>{rowData.name}</span>}
                  ></Column>
                  <Column
                    style={{ width: "60px" }}
                    header="Actions"
                    body={(rowData) => (
                      <Button
                        icon="pi pi-times"
                        className=" p-button-sm p-button-rounded p-button-danger p-button-text"
                        aria-label="Cancel"
                        type="button"
                        onClick={() => {
                          if (employeeId) {
                            handleDeleteAttachment("otherDocumentsArr", [
                              rowData.path,
                            ]);
                          }
                          const docsList = otherDocumentsArr.filter(
                            (_doc) => _doc.name !== rowData.name
                          );
                          setValue("otherDocumentsArr", docsList);
                        }}
                      />
                    )}
                  ></Column>
                </DataTable>
              </div>
            </div>
          </div>
        </FormFieldsPanel>
      </div>
      <div className={classes.actions}>
        <Button
          label="Cancel"
          type="reset"
          className="p-button-outlined p-button-secondary"
          onClick={() => navigate("/management/employees")}
        />
        <Button
          label={employeeId ? "Update" : "Save"}
          type="submit"
          disabled={isPending}
        />
      </div>
      <If condition={openMultipleDocsPopup}>
        <AttachMultipleDocsPopup
          onValueSet={handleAddOtherDocuments}
          onHide={() => setOpenMultipleDocsPopup(false)}
          isEmployeeForm
        />
      </If>
      <EmailChangeConfirmDialog
        visible={pendingData !== null}
        newEmail={pendingData?.email ?? ""}
        onCancel={() => setPendingData(null)}
        onConfirm={(notify) => {
          submitEmployee(pendingData, notify);
          setPendingData(null);
        }}
      />
    </form>
  );
};
